import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API } from '../lib/api';

/**
 * Injects a per-tenant PWA manifest + apple-touch-icon so that when the user
 * adds the page to the mobile home screen (Safari iOS / Chrome Android), the
 * shortcut gets the tenant's subdomain (slug) as name and the company logo as icon.
 *
 * iOS Safari is picky: it primarily reads <link rel="apple-touch-icon"> for the
 * home-screen icon and IGNORES manifest icons. It also caches icons aggressively
 * — so we set the icon with a stable href (preserving ?v=X if logo changes).
 */
const setOrCreateLink = (rel, href, attrs = {}) => {
  let link = document.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
  Object.entries(attrs).forEach(([k, v]) => link.setAttribute(k, v));
  return link;
};

export const TenantManifest = ({ target = 'erp' }) => {
  const { slug } = useParams();

  useEffect(() => {
    if (!slug) return;
    const manifestUrl = `${API}/tenant/manifest?slug=${encodeURIComponent(slug)}&target=${encodeURIComponent(target)}`;
    const brandingUrl = `${API}/tenant/branding?slug=${encodeURIComponent(slug)}`;

    const origManifest = document.querySelector('link[rel="manifest"]')?.getAttribute('href');
    const origAppleIcon = document.querySelector('link[rel="apple-touch-icon"]')?.getAttribute('href');
    const origIcon = document.querySelector('link[rel="icon"]')?.getAttribute('href');
    const origTitle = document.title;

    // Set manifest immediately (so Safari picks it up if install is triggered)
    setOrCreateLink('manifest', manifestUrl);

    // Fetch branding for title + icon (faster than manifest endpoint)
    fetch(brandingUrl)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const name = data.company_name || slug;
        document.title = name;
        // Also set OG meta if needed
        const logo = data.logo_url;
        if (logo) {
          const absLogo = logo.startsWith('http') ? logo : `${window.location.origin}${logo}`;
          // iOS Safari primary
          setOrCreateLink('apple-touch-icon', absLogo, { sizes: '180x180' });
          // Standard favicon fallback (Chrome tab + bookmark)
          setOrCreateLink('icon', absLogo, { type: 'image/png' });
          setOrCreateLink('shortcut icon', absLogo);
          // Preload so Safari doesn't race when user taps "Add to Home"
          const preload = document.createElement('link');
          preload.rel = 'preload';
          preload.as = 'image';
          preload.href = absLogo;
          document.head.appendChild(preload);
          setTimeout(() => { try { document.head.removeChild(preload); } catch { /* ignore */ } }, 5000);
        }
      })
      .catch(() => { /* ignore */ });

    return () => {
      // Restore original references on unmount
      if (origManifest) setOrCreateLink('manifest', origManifest);
      if (origAppleIcon) setOrCreateLink('apple-touch-icon', origAppleIcon);
      if (origIcon) setOrCreateLink('icon', origIcon);
      if (origTitle) document.title = origTitle;
    };
  }, [slug, target]);

  return null;
};
